import React, { useEffect, useState, useRef, useMemo } from "react";
import { string, bool, arrayOf, shape, objectOf } from "prop-types";

import cx from "classnames";

import initialState from "./initialState.js";

import fetchSearchResults from "../../common/fetchSearchResults.js";
import {
  initCurrentParams,
  createQueryString,
} from "../../common/dynamicSearch.js";

import { scrollToId } from "../../common/scrollHelpers.js";

import ArrowLink from "../ArrowLink/ArrowLink.jsx";

import Pagination from "../Pagination/Pagination.jsx";
import ResultsInfo from "../ResultsInfo/ResultsInfo.jsx";
import SimpleSearchBox from "../SimpleSearchBox/SimpleSearchBox.jsx";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner.jsx";
import GrantsCallout from "./GrantsCallout.jsx";
import AdditionalPrograms from "./AdditionalPrograms.jsx";
import ManualContentListing from "../ManualContentListing/ManualContentListing.jsx";

const propTypes = {
  id: string,
  title: string,
  enableQueryString: bool,
  useRightSidebar: bool,
  pagination: bool,
  search: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  type: string,
  grantSearchUrl: string,
  dictionary: objectOf(string).isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    loadAllPages: bool,
    pageId: string,
  }).isRequired,
  seeMore: shape({ href: string, text: string }),
  sorters: arrayOf(
    shape({
      direction: string,
      label: string,
      value: string,
      isActive: bool,
      id: string,
    }),
  ),
};

const SearchComponent = (props) => {
  const {
    id,
    title,
    enableQueryString,
    useRightSidebar,
    pagination,
    search,
    hideMobileSummary,
    url,
    type,
    dictionary,
    query,
    grantSearchUrl,
    seeMore,
    sorters,
  } = props;

  const activeSorterParam = useMemo(() => {
    const activeSorter = sorters.find((sorter) => sorter.isActive) || null;

    return activeSorter
      ? {
          sortBy: activeSorter.value,
          sortOrder: activeSorter.direction,
        }
      : null;
  }, [sorters]);

  const generalParams = {
    ...initialState.query,
    ...query,
    ...activeSorterParam,
  };

  const [showOverlay, setShowOverlay] = useState(false);
  const [serverData, setServerData] = useState(null);

  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  const resultsFocusRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  useEffect(() => {
    if (new URL(document.location).searchParams.get("q")) {
      scrollToId(id);
      setTimeout(() => {
        document.querySelector(`#${id} .simple-search-box__input`).focus();
      }, 1000);
    }
  }, [id]);

  //update the url with the latest query parameters
  useEffect(() => {
    if (enableQueryString) {
      const queryString = createQueryString(currentParams);
      if (queryString.length > 0) {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            "?" +
            createQueryString(currentParams) +
            window.location.hash,
        );
      } else {
        window.history.replaceState(
          "",
          "",
          window.location.origin +
            window.location.pathname +
            window.location.hash,
        );
      }
    }
  }, [currentParams, enableQueryString]);

  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change
      resultsFocusRef.current.focus();
      showFocusRef.current.showFocus = false;
    }
  }, [serverData]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults(
      url,
      {
        ...currentParams,
      },
      type,
      signal,
    )
      .then(function (data) {
        if (data) {
          setServerData(data);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, type, url]);

  const handleQueryChange = (term) => {
    if (currentParams.q === term) {
      return;
    }
    setCurrentParams((prevState) => {
      return { ...prevState, q: term, page: 1 };
    });
  };

  const handlePaginationChange = (page) => {
    showFocusRef.current.showFocus = true;
    setCurrentParams((prevState) => {
      return { ...prevState, page: page };
    });
    scrollToId(`${id}-results`);
  };

  const classModifier = `dynamic-content-listing--${type}`;
  const showSortersArea = serverData && sorters && sorters.length > 0;

  return (
    <div
      className={`search-component dynamic-content-listing ${classModifier}`}
      id={id}
    >
      {title && <h1 className="search-component__heading">{title}</h1>}
      {search && (
        <div className="search-component__search-box">
          <SimpleSearchBox
            searchTerm={currentParams.q}
            onTextChange={handleQueryChange}
            placeholder={dictionary.searchPlaceholder}
          />
        </div>
      )}

      <div className="search-component__results-wrapper">
        <div className="search-component__results">
          {serverData &&
            showSortersArea &&
            (type === "default" ||
              type === "grantee" ||
              type === "search-component") && (
              <div className="dynamic-content-listing__sorters-wrapper">
                <div className="dynamic-content-listing__results-info">
                  <ResultsInfo
                    totalResults={serverData.totalResults}
                    request={serverData.request}
                    resultsInfoLabel={dictionary.resultsInfoLabel || "Showing"}
                    query={currentParams.q}
                    clearAllFacetsLabel={dictionary.clearAll}
                  />
                </div>
              </div>
            )}

          {serverData && serverData.results && (
            <div
              className={cx("dynamic-content-listing__results", {
                "dynamic-content-listing__results--no-mobile-summaries":
                  hideMobileSummary,
              })}
            >
              {showOverlay && <LoadingSpinner />}
              <section className="component">
                {serverData.results.length > 0 && (
                  <ManualContentListing
                    results={[serverData.results[0]]}
                    focusRef={resultsFocusRef}
                  />
                )}
                {serverData.grantBox &&
                  serverData.grantBox.numberOfGrants > 0 &&
                  currentParams.page === 1 && (
                    <GrantsCallout
                      grantBox={serverData.grantBox}
                      query={currentParams.q}
                      grantSearchUrl={grantSearchUrl}
                    />
                  )}
                {serverData.results.length > 1 && (
                  <ManualContentListing results={serverData.results.slice(1)} />
                )}
              </section>
            </div>
          )}
          {serverData &&
            pagination &&
            serverData.totalResults > serverData.request.perPage && (
              <div
                className={cx("dynamic-content-listing__pagination", {
                  "dynamic-content-listing__pagination--centered":
                    !seeMore.href,
                })}
              >
                <Pagination
                  totalResults={serverData.totalResults}
                  //this is currently returned as a string from the server, so convert to number
                  resultsPerPage={Number(serverData.request.perPage)}
                  currentPage={Number(currentParams.page)}
                  onPaginationChange={handlePaginationChange}
                />
                {seeMore.href && (
                  <div className="dynamic-content-listing__more">
                    <ArrowLink {...seeMore} />
                  </div>
                )}
              </div>
            )}
        </div>
        <div className="search-component__related">
          {serverData &&
            serverData.sidebar &&
            serverData.totalResults > 0 &&
            currentParams.page === 1 &&
            useRightSidebar === true && (
              <AdditionalPrograms sidebar={serverData.sidebar} />
            )}
        </div>
      </div>
    </div>
  );
};

SearchComponent.propTypes = propTypes;
export default SearchComponent;
